import * as API from '@/API';
import type CheckModel from '@/Models/Check/CheckModel';
import type FilingCabinetStore from '@/Stores/FilingCabinetStore';
import type { ColumnSort, PaginationState } from '@tanstack/react-table';
import { action, computed, makeObservable, observable } from 'mobx';

interface StateData {
    checks: CheckModel[];
    pagination: PaginationState;
    sorting: ColumnSort[];
    nameFilter: string;
    validFilter: boolean;
}

export default class ChecksStore {
    state: StateData = {
        nameFilter: '',
        validFilter: false,
        checks: [],
        pagination: { pageIndex: 0, pageSize: 25 },
        sorting: [],
    };

    loading = true;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(fcStore: FilingCabinetStore<StateData>) {
        this.fcStore = fcStore;
        makeObservable(this, {
            state: observable,
            loading: observable,
            filteredChecks: computed,
            set: action,
            setChecks: action,
            setState: action,
            setPagination: action,
            setSorting: action,
            reload: action,
            setLoading: action,
        });
        this.fcStore.initializeState(this.initOld, this.initNew);
    }

    private readonly initOld = (state: StateData): void => {
        this.setState({
            checks: state.checks,
            sorting: state.sorting,
            pagination: state.pagination,
            nameFilter: state.nameFilter,
            validFilter: state.validFilter,
        });
        API.checks.getChecksOverview()
            .then((res) => {
                this.setChecks(res);
                this.setLoading(false);
            })
            .catch((err) => console.error('Could not reload CheckOverview', err));
    };

    private readonly initNew = (): void => {
        this.setLoading(true);
        API.checks.getChecksOverview()
            .then((res) => {
                this.setState({
                    checks: res,
                    nameFilter: '',
                    pagination: { pageIndex: 0, pageSize: 25 },
                    sorting: [],
                    validFilter: false,
                });
                this.fcStore.updateTitle('Checks');
                this.setLoading(false);
            });
    };

    reload = (): void => {
        this.setChecks([]);
        this.setLoading(true);
        API.checks.getChecksOverview()
            .then((res) => {
                this.setChecks(res);
                this.setLoading(false);
            })
            .catch((err) => console.error('Could not reload CheckOverview', err));
    };

    setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    set = <K extends keyof StateData>(prop: K, val: any): void => {
        this.state[prop] = val;
        this.fcStore.updateState(this.state);
    };

    setChecks = (val: CheckModel[]): void => {
        this.state.checks = val;
        this.fcStore.updateState(this.state);
    };

    setPagination = (val: PaginationState): void => {
        this.state.pagination = val;
        this.fcStore.updateState(this.state);
    };

    setSorting = (val: ColumnSort[]): void => {
        this.state.sorting = val;
        this.fcStore.updateState(this.state);
    };

    setLoading = (val: boolean): void => {
        this.loading = val;
    };

    get filteredChecks(): CheckModel[] {
        const searchTerm = this.state.nameFilter.toLowerCase();
        const now = new Date();
        return this.state.checks.filter((row: CheckModel) => {
            let matches = false;
            if (searchTerm !== '') {
                const value = row['Name' as keyof CheckModel];
                if (value !== undefined && value !== null) {
                    if (typeof value === 'string' && (value.toLowerCase().includes(searchTerm))) {
                        matches = true;
                    }
                }
            }
            else {
                matches = true;
            }
            if (this.state.validFilter && matches) {
                const fromValue = row.ValidFrom;
                const toValue = row.ValidTo;
                const fromDate = new Date(fromValue ?? 0);
                const toDate = new Date(toValue ?? '3000-01-01');
                if (now > toDate || now < fromDate) {
                    matches = false;
                }
            }
            return matches;
        });
    }
};
